import { useEffect } from 'react';

/**
 * While an interactive modal (Radix Dialog / AlertDialog) is open above the
 * Dayboard, the board must not begin — or continue — any drag of its own
 * from pointer activity: a drag inside the modal (the Guided Start /
 * Birdseye bay picker map, its title, its body) is the modal's, never the
 * board's.
 *
 * The board's drag systems install DOCUMENT-level move listeners, so checking
 * only where a press started is not enough. Every mounted dialog content holds
 * this lock (see ui/dialog.tsx and ui/alert-dialog.tsx); the Dayboard's
 * drag-to-scroll refuses to start or continue while it is held, and Embla's
 * watchDrag consults it per press. Nothing here disables board dragging when
 * no dialog is open.
 */
let holders = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const l of [...listeners]) {
    try { l(); } catch { /* a listener must not break the lock */ }
  }
}

/** Take the lock; returns the release function (idempotent). */
export function acquireDayboardOverlayLock(): () => void {
  holders += 1;
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders = Math.max(0, holders - 1);
    emit();
  };
}

export function isDayboardOverlayLocked(): boolean {
  return holders > 0;
}

/** Subscribe to lock changes; returns the unsubscribe function. */
export function subscribeDayboardOverlayLock(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Holds the lock for as long as the calling component is mounted (and `active`). */
export function useDayboardOverlayLock(active = true): void {
  useEffect(() => {
    if (!active) return;
    return acquireDayboardOverlayLock();
  }, [active]);
}

/** Test-only: reset module state between tests. */
export function __resetDayboardOverlayLockForTests(): void {
  holders = 0;
  listeners.clear();
}
